import type { TrackPoint } from "@/lib/client-convert";

// A trim range expressed as second offsets from the activity start, so it is
// independent of the file's sample rate and supports exact, typed entry.
export type TrimRange = { startSec: number; endSec: number };

export type ResolvedTrim = {
  totalSec: number;
  startSec: number;
  endSec: number;
  startMs: number;
  endMs: number;
  isTrimmed: boolean;
};

export function clamp(n: number, lo: number, hi: number): number {
  return Math.min(Math.max(n, lo), hi);
}

// Clamp a stored range to the activity's bounds and resolve it to a concrete
// time window. An uninitialized/collapsed range (endSec <= startSec) is treated
// as the full activity. The extremes snap to the real first/last timestamps so
// an untrimmed conversion stays exact despite whole-second rounding.
export function resolveTrim(points: TrackPoint[], trim?: TrimRange): ResolvedTrim {
  const base = points[0]?.timeMs ?? 0;
  const lastMs = points[points.length - 1]?.timeMs ?? base;
  const totalSec = points.length >= 2 ? Math.max(0, Math.round((lastMs - base) / 1000)) : 0;

  const startSec = clamp(Math.round(trim?.startSec ?? 0), 0, totalSec);
  let endSec = clamp(Math.round(trim?.endSec ?? 0), 0, totalSec);
  if (endSec <= startSec) endSec = totalSec;

  const isTrimmed = startSec > 0 || endSec < totalSec;
  const startMs = startSec <= 0 ? base : base + startSec * 1000;
  const endMs = endSec >= totalSec ? lastMs : base + endSec * 1000;
  return { totalSec, startSec, endSec, startMs, endMs, isTrimmed };
}

// Parse "ss", "m:ss" or "h:mm:ss" into seconds. Returns null on malformed input.
export function parseDuration(input: string): number | null {
  const parts = input.trim().split(":").map((p) => p.trim());
  if (parts.length > 3) return null;
  if (parts.some((p) => !/^\d+$/.test(p))) return null;
  const nums = parts.map(Number);
  if (nums.length === 1) return nums[0];
  if (nums.length === 2) return nums[0] * 60 + nums[1];
  return nums[0] * 3600 + nums[1] * 60 + nums[2];
}
